import { useTranslation } from "react-i18next";
import { KTSVG } from "../../../../../../../../_metronic/helpers";
import { useViewAmbientContext } from "../AmbientsContext";
import { useIsMobile } from "../../../../../../../shared/responsive";
import { useViewImageContext } from "../../Images/ViewImageContext";
import { useViewLandContext } from "../../ViewLandContext";
import { PLANET_HD_V1_ENABLED } from "../../../../../../../config/featureFlags";

const AmbientActionIcon = ({ isHd = false, mobile = false }) => (
  <span
    className={[
      mobile ? "view-land-mobile-state-card__icon" : "svg-icon svg-icon-warning me-5 view-land-ambient-desktop-icon",
      isHd ? "view-land-ambient-icon--hd" : "",
    ].filter(Boolean).join(" ")}
  >
    <KTSVG path="/media/icons/duotune/general/gen055.svg" className={mobile ? "svg-icon svg-icon-2" : ""} />
    {isHd && <span className="view-land-ambient-hd-badge">HD</span>}
  </span>
);

const MobileAmbientButton = ({ title, status, active, disabled, isHd = false, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`view-land-mobile-state-card view-land-mobile-state-card--button ${active ? "active" : ""} ${disabled ? "disabled" : ""}`.trim()}
  >
    <span className="view-land-mobile-state-card__header">
      <AmbientActionIcon isHd={isHd} mobile />
      <span className="view-land-mobile-state-card__copy">
        <span className="view-land-mobile-state-card__title fs-7">{title}</span>
        <span className="view-land-mobile-state-card__status fs-7">{status}</span>
      </span>
    </span>
  </button>
);

const DesktopAmbientButton = ({ title, active, disabled, isHd = false, onClick }) => (
  <div
    onClick={disabled ? undefined : onClick}
    className={`d-flex align-items-center div-type-selector rounded p-5 ${disabled ? 'disabled' : ''} ${active ? 'active' : ''}`.trim()}
  >
    <AmbientActionIcon isHd={isHd} />
    <div className="flex-grow-1 me-2">
      <span className="fw-bold text-gray-800 text-hover-primary fs-7">{title}</span>
      {active && <span className='spinner-border spinner-border-sm align-middle ms-2'></span>}
    </div>
  </div>
);

const HdFailureNotice = ({ message }) => (
  <div
    className="alert alert-danger view-land-ambient-failure-notice py-3 px-4 mb-3"
    role="status"
  >
    {message}
  </div>
);

const NoAmbient = () => {
  const { t } = useTranslation();
  const {
    createTriggered,
    create,
    createHd,
    ambientSummary,
    openAmbientDialog,
    canAccessHistoricalHd,
  } = useViewAmbientContext();
  const viewImageContext = useViewImageContext();
  const viewLandContext = useViewLandContext();
  const isMobile = useIsMobile();
  const hdAccess = viewImageContext.getHdAccessStatus(viewLandContext.land?.land_id);
  const hasStandardAmbient = ambientSummary.hasHistoricalAmbient;
  const hasHdAmbient = ambientSummary.hasHistoricalHdAmbient;
  const hasFailedHdAmbient = ambientSummary.hasFailedHistoricalHdJob;
  const standardProcessing = ambientSummary.isHistoricalAmbientProcessing;
  const hdProcessing = ambientSummary.isHistoricalHdAmbientProcessing;
  const hdUnavailable = hdAccess.loading || !hdAccess.enabled;
  const standardRequestInFlight = ['default', 'personalized'].includes(createTriggered);
  const hdRequestInFlight = ['default_hd', 'personalized_hd'].includes(createTriggered);
  const standardAutomaticDisabled = standardProcessing || standardRequestInFlight;
  const standardPersonalizedDisabled = standardProcessing || standardRequestInFlight;
  const hdAutomaticDisabled = hdUnavailable || hdProcessing || hdRequestInFlight;
  const hdPersonalizedDisabled = hdUnavailable || hdProcessing || hdRequestInFlight;
  const hdAutomaticTitle = hasFailedHdAmbient
    ? t("ambient:view.buttons.retry_hd")
    : t("ambient:view.buttons.create_hd");
  const hdFailureMessage = t("ambient:view.messages.hd_creation_failed_message");

  if (isMobile) {
    return (
      <div className="view-land-mobile-state-stack">
        {hasFailedHdAmbient && <HdFailureNotice message={hdFailureMessage} />}
        {!hasStandardAmbient && !standardProcessing && (
          <MobileAmbientButton
            title={t("ambient:view.buttons.create")}
            status={createTriggered === "default" ? t("ambient:view.status.creating") : t("ambient:view.status.available")}
            active={createTriggered === "default"}
            disabled={standardAutomaticDisabled}
            onClick={() => create("default", {})}
          />
        )}

        {!hasStandardAmbient && !standardProcessing && (
          <MobileAmbientButton
            title={t("ambient:view.buttons.create_personalized")}
            status={t("ambient:view.status.available")}
            active={createTriggered === "personalized"}
            disabled={standardPersonalizedDisabled}
            onClick={() => openAmbientDialog("standard")}
          />
        )}

        {PLANET_HD_V1_ENABLED && canAccessHistoricalHd && !hasHdAmbient && !hdProcessing && (
          <MobileAmbientButton
            title={hdAutomaticTitle}
            status={hdUnavailable ? t("ambient:view.status.hd_disabled") : t("ambient:view.status.available")}
            active={createTriggered === "default_hd"}
            disabled={hdAutomaticDisabled}
            isHd
            onClick={() => createHd("default_hd", {})}
          />
        )}

        {PLANET_HD_V1_ENABLED && canAccessHistoricalHd && !hasHdAmbient && !hdProcessing && (
          <MobileAmbientButton
            title={t("ambient:view.buttons.create_personalized_hd")}
            status={hdUnavailable ? t("ambient:view.status.hd_disabled") : t("ambient:view.status.available")}
            active={createTriggered === "personalized_hd"}
            disabled={hdPersonalizedDisabled}
            isHd
            onClick={() => openAmbientDialog("hd")}
          />
        )}
      </div>
    );
  }

  return (
    <>
      {hasFailedHdAmbient && <HdFailureNotice message={hdFailureMessage} />}
      {!hasStandardAmbient && !standardProcessing && (
        <DesktopAmbientButton
          title={t("ambient:view.buttons.create")}
          active={createTriggered === 'default'}
          disabled={standardAutomaticDisabled}
          onClick={() => create('default', {})}
        />
      )}
      {!hasStandardAmbient && !standardProcessing && (
        <DesktopAmbientButton
          title={t("ambient:view.buttons.create_personalized")}
          active={createTriggered === 'personalized'}
          disabled={standardPersonalizedDisabled}
          onClick={() => openAmbientDialog("standard")}
        />
      )}
      {PLANET_HD_V1_ENABLED && canAccessHistoricalHd && !hasHdAmbient && !hdProcessing && (
        <DesktopAmbientButton
          title={hdAutomaticTitle}
          active={createTriggered === 'default_hd'}
          disabled={hdAutomaticDisabled}
          isHd
          onClick={() => createHd('default_hd', {})}
        />
      )}
      {PLANET_HD_V1_ENABLED && canAccessHistoricalHd && !hasHdAmbient && !hdProcessing && (
        <DesktopAmbientButton
          title={t("ambient:view.buttons.create_personalized_hd")}
          active={createTriggered === 'personalized_hd'}
          disabled={hdPersonalizedDisabled}
          isHd
          onClick={() => openAmbientDialog("hd")}
        />
      )}
    </>
  );
};

export default NoAmbient;
